import type { MentionPerson } from './types';

/**
 * Tags travel as `<@userId>` — the backend reads who was tagged from the body
 * itself. Nobody should type or read that token, so text being edited shows
 * `@Name` instead and is converted back on the way out.
 */
export const MENTION_TOKEN = /<@([^>\s]+)>/g;

const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** A stored body → the text a person edits: every token becomes `@Name`. */
export function toEditableText(body: string, mentions: MentionPerson[]): string {
	const names = new Map(mentions.map((m) => [m.userId, m.name]));
	return body.replace(MENTION_TOKEN, (token, id: string) => {
		const name = names.get(id);
		return name ? `@${name}` : token;
	});
}

/**
 * Edited text → the body to send: every `@Name` of someone picked becomes their
 * token. Longest names first, so "@Ana Silva" isn't claimed by a person called
 * "Ana". Only exact picked names convert — a stray "@" stays text.
 */
export function toBodyText(text: string, people: MentionPerson[]): string {
	const unique = new Map(people.map((p) => [p.userId, p]));
	const sorted = [...unique.values()].sort((a, b) => b.name.length - a.name.length);
	let out = text;
	for (const p of sorted) {
		const re = new RegExp(`(^|[^\\w])@${escapeRegExp(p.name)}(?![\\w])`, 'g');
		out = out.replace(re, (_m, lead: string) => `${lead}<@${p.userId}>`);
	}
	return out.trim();
}

/** A body split into text and tag parts, for rendering chips. */
export type BodyPart =
	| { kind: 'text'; text: string }
	| { kind: 'mention'; userId: string; name: string };

export function splitBody(body: string, mentions: MentionPerson[]): BodyPart[] {
	const names = new Map(mentions.map((m) => [m.userId, m.name]));
	const parts: BodyPart[] = [];
	let last = 0;
	for (const match of body.matchAll(MENTION_TOKEN)) {
		const at = match.index ?? 0;
		if (at > last) parts.push({ kind: 'text', text: body.slice(last, at) });
		const userId = match[1];
		// Never show a raw token: someone no longer resolvable reads as "someone".
		parts.push({ kind: 'mention', userId, name: names.get(userId) ?? 'someone' });
		last = at + match[0].length;
	}
	if (last < body.length) parts.push({ kind: 'text', text: body.slice(last) });
	return parts;
}
